import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  Heading,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import {
  CompanyCapitalType,
  FundraisingStatus,
  useGetCompanyQuery,
  useGetFundraisingQuery,
} from '@blockpulse3/graphql/hooks';

import { FundraisingSubscribersPanel } from './FundraisingSubscribersPanel';
import { checkSubscriberDefaultValues, checkSubscriberSchema } from './schema';
import { IFundraisingSubscriberCheck } from './types';

const MAX_SUBSCRIBERS = 149;

type Props = unknown;

/**
 * NewPrivateFundraisingSubscribers.
 * Second step of fundraising funnel, subscribers. Splitted into modal and table components.
 *
 * @returns {JSX.Element}
 */
export function NewPrivateFundraisingSubscribers(): JSX.Element {
  const i18nFundraisingSubscribersForm = useTranslations('FundraisingSubscribersForm');

  const navigate = useNavigate();
  const { fundraisingId = '', companyId = '' } = useParams();
  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const { data, loading } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    skip: !fundraisingId,
    onCompleted: (data) => {
      if (data.fundraising.status !== FundraisingStatus.DRAFT) {
        navigate(routes.workspace.fmt(companyId));
      }
    },
    onError: () => {
      navigate(routes.workspace.fmt(companyId));
    },
  });

  const fundraising = data?.fundraising;
  const fundraisingRoutes = routes.workspace.fundraising.private;

  const subscribersCount = fundraising?.subscriptionCount || 0;
  const isMaxSubsribers = subscribersCount > MAX_SUBSCRIBERS;
  const isSubscribersListEmpty = subscribersCount === 0;

  /* ** Max subscriber check form ** */
  const { register, formState, handleSubmit, watch, setValue } =
    useForm<IFundraisingSubscriberCheck>({
      defaultValues: checkSubscriberDefaultValues,
      resolver: yupResolver(checkSubscriberSchema),
    });
  const isChecked = watch('maxSubscribers');

  const handleFormCancel = (): void => {
    if (!company) return;
    if (company.capitalType === CompanyCapitalType.FIXED) {
      navigate(fundraisingRoutes.iban.fmt(companyId, fundraisingId));
    } else {
      navigate(fundraisingRoutes.documentation.fmt(companyId, fundraisingId));
    }
  };

  const handleFormSubmit: SubmitHandler<IFundraisingSubscriberCheck> = (): void => {
    navigate(routes.workspace.fundraising.private.summary.fmt(companyId, fundraisingId));
  };

  useEffect(() => {
    setValue('maxSubscribers', !isMaxSubsribers);
  }, [setValue, isMaxSubsribers]);

  return (
    <Skeleton isLoaded={!loading} w="full">
      <Card variant="divider-top">
        <CardHeader as={Stack} spacing="2">
          <Heading size="lg">{i18nFundraisingSubscribersForm('FormTitle')}</Heading>
          <Text color="gray.600" fontSize="lg">
            {i18nFundraisingSubscribersForm('FormDescription')}
          </Text>
        </CardHeader>
        <Divider />
        <CardBody>
          <Stack spacing={isMaxSubsribers ? '8' : '0'}>
            <FundraisingSubscribersPanel />
            <form id="fundraising-subscribers" onSubmit={handleSubmit(handleFormSubmit)}>
              <Stack spacing="8">
                {isMaxSubsribers && (
                  <FormControl isInvalid={!!formState.errors.maxSubscribers}>
                    <Checkbox
                      alignItems="flex-start"
                      isChecked={isChecked}
                      {...register('maxSubscribers')}
                    >
                      <Stack spacing="1">
                        <Text color="gray.700" fontWeight="500" lineHeight="1.2">
                          {i18nFundraisingSubscribersForm('Alert.LengthCheckboxTitle', {
                            max: MAX_SUBSCRIBERS,
                          })}
                        </Text>
                        <Text color="gray.500" fontWeight="400" lineHeight="1.2">
                          {i18nFundraisingSubscribersForm('Alert.LengthCheckboxSubTitle')}
                        </Text>
                      </Stack>
                    </Checkbox>
                  </FormControl>
                )}
              </Stack>
            </form>
          </Stack>
        </CardBody>
        <CardFooter as={Stack} direction={{ base: 'column', md: 'row' }} spacing="4">
          <Button variant="secondary" w="full" onClick={handleFormCancel}>
            {i18nFundraisingSubscribersForm('Back')}
          </Button>
          <Button
            data-cy="next"
            form="fundraising-subscribers"
            isDisabled={isSubscribersListEmpty}
            type="submit"
            w="full"
          >
            {i18nFundraisingSubscribersForm('Next')}
          </Button>
        </CardFooter>
      </Card>
    </Skeleton>
  );
}

export type NewPrivateFundraisingSubscribersProps = Props;
